import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ApiCall from "../common/ApiCall";
import { getCurrentUser, getAuthToken } from "../common/Helper";

const AvatarModal = ({
  show,
  onHide,
  onSelect,
  avatars,
  isLoading,
  selectedAvatar,
  showTopButtons = true,
}) => {
  const navigate = useNavigate();

  const [view, setView] = useState("select"); // "select" or "generate"
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAvatarUrl, setGeneratedAvatarUrl] = useState("");
  const [aiUsage, setAiUsage] = useState({ used: 0, limit: 0, remaining: 0 });
  const [error, setError] = useState("");

  const currentUser = getCurrentUser();
  const userPermissions = currentUser?.permissions || [];
  const hasAIPermission = userPermissions.includes(
    "frontend.avatar_ai_generator",
  );

  const fetchUsage = async () => {
    try {
      const res = await ApiCall("GET", "avatar/usage", null, getAuthToken());
      if (res?.data?.success) {
        setAiUsage(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching AI avatar usage:", err);
    }
  };

  useEffect(() => {
    if (show) {
      setView("select");
      setPrompt("");
      setGeneratedAvatarUrl("");
      setError("");
      if (hasAIPermission) {
        fetchUsage();
      }
    }
  }, [show, hasAIPermission]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setIsGenerating(true);
    setError("");
    try {
      const res = await ApiCall(
        "POST",
        "avatar/generate",
        { prompt },
        getAuthToken(),
      );
      if (res?.data?.success) {
        const newUrl = res.data.data.url;
        setGeneratedAvatarUrl(newUrl);
        if (res.data.data.usage) {
          setAiUsage(res.data.data.usage);
        }
      } else {
        setError(res?.data?.message || "Failed to generate AI avatar");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "An error occurred during generation.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      {show && (
        <div
          className="modal fade custom-modal show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-scrollable modal-lg">
            <div className="modal-content custom-modal">
              <div className="modal-header custom-modal-header">
                <h5 className="modal-title">
                  {view === "generate"
                    ? "AI Avatar Generator"
                    : "Choose Your Avatar"}
                </h5>
                <button
                  type="button"
                  className="btn-close-avatar-modal"
                  onClick={onHide}
                  aria-label="Close"
                >
                  x
                </button>
              </div>
              <div className="modal-body custom-modal-body">
                {view === "select" ? (
                  <>
                    {/* Upsell Buttons */}
                    {showTopButtons && currentUser && (
                      <div className="row g-3 mb-4">
                        <div className="col-12 col-md-6">
                          <button
                            className="btn w-100 py-3 px-4 btn-upsell btn-upsell-purchase"
                            onClick={() => {
                              onHide();
                              navigate("/shop");
                            }}
                          >
                            <i
                              className="fa-solid fa-lock me-2"
                              style={{ fontSize: "1.1rem" }}
                            ></i>
                            Purchase Avatars
                          </button>
                        </div>
                        <div className="col-12 col-md-6">
                          {hasAIPermission ? (
                            <button
                              className="btn w-100 py-3 px-4 btn-upsell btn-upsell-ai"
                              onClick={() => setView("generate")}
                            >
                              <i
                                className="fa-solid fa-bolt me-2"
                                style={{ fontSize: "1.1rem" }}
                              ></i>
                              Avatar AI Generator
                            </button>
                          ) : (
                            <button
                              className="btn w-100 py-3 px-4 btn-upsell btn-upsell-generate"
                              onClick={() => {
                                onHide();
                                navigate("/memberships");
                              }}
                            >
                              <i
                                className="fa-solid fa-bolt me-2"
                                style={{ fontSize: "1.1rem" }}
                              ></i>
                              Upgrade to Generate Avatars
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {isLoading ? (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading avatars...</p>
                      </div>
                    ) : !Array.isArray(avatars) || avatars.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="text-muted mb-3">
                          <i
                            className="bi bi-emoji-frown"
                            style={{ fontSize: "3rem" }}
                          ></i>
                        </div>
                        <h5>No avatars available</h5>
                        <p className="text-muted">Please try again later</p>
                      </div>
                    ) : (
                      <div className="row" id="avatar_list">
                        {avatars.map((avatar) => (
                          <div
                            key={avatar.id}
                            className="col-4 col-sm-3 col-md-2 mb-3 text-center position-relative"
                          >
                            <img
                              src={avatar.url}
                              alt={`Avatar ${avatar.id}`}
                              className="img-thumbnail cursor-pointer avatar-option"
                              onClick={() => onSelect(avatar.url)}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform =
                                  "scale(1.05)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ai-avatar-generator-container p-2">
                    <div className="d-flex align-items-center mb-4">
                      <button
                        className="btn btn-link text-decoration-none p-0 text_blue_custom font-semibold d-flex align-items-center"
                        onClick={() => {
                          setView("select");
                          setPrompt("");
                          setGeneratedAvatarUrl("");
                          setError("");
                        }}
                      >
                        <i className="fa-solid fa-arrow-left me-1"></i> Back to
                        options
                      </button>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-4">
                        <div className="card card-ai-generator p-4 shadow-sm h-100">
                          <h5 className="font-semibold mb-2">
                            Describe your avatar
                          </h5>
                          <p className="text-muted small mb-3">
                            Describe what you want your avatar to look like. All
                            avatars are generated in our signature{" "}
                            <strong>minimalist cartoon vector style</strong>{" "}
                            with a colored circular backdrop.
                          </p>

                          <form onSubmit={handleGenerate}>
                            <div className="mb-3">
                              <textarea
                                className="form-control"
                                rows="3"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="e.g. Alien avatar, cute purple slime avatar, minimal astronaut cat..."
                                disabled={isGenerating}
                                style={{
                                  borderRadius: "12px",
                                  border: "1px solid #ced4da",
                                }}
                              />
                            </div>

                            {/* Preset Inspiration Chips */}
                            <div className="mb-3">
                              <span className="small text-muted d-block mb-1 font-semibold">
                                Try an idea:
                              </span>
                              <div className="d-flex flex-wrap gap-1">
                                {[
                                  "👽 Alien avatar",
                                  "🧪 Slime avatar",
                                  "🐱 Cute cat avatar",
                                  "🤖 Friendly robot",
                                  "🧙 Minimal wizard",
                                  "👨‍🚀 Astronaut avatar",
                                ].map((chip) => (
                                  <button
                                    key={chip}
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary py-1 px-2 small"
                                    style={{
                                      borderRadius: "20px",
                                      fontSize: "0.78rem",
                                    }}
                                    onClick={() => setPrompt(chip)}
                                    disabled={isGenerating}
                                  >
                                    {chip}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {error && (
                              <div
                                className="alert alert-danger py-2 px-3 mb-3 small"
                                style={{ borderRadius: "8px" }}
                              >
                                {error}
                              </div>
                            )}

                            {aiUsage && (
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="small text-muted">
                                  Monthly Usage:{" "}
                                  <strong className="text_blue_custom">
                                    {aiUsage.limit === -1 ? (
                                      <i class="fa-solid fa-infinity"></i>
                                    ) : (
                                      `${aiUsage.used}/${aiUsage.limit}`
                                    )}
                                  </strong>{" "}
                                  generated
                                </span>
                                <span className="small text-muted">
                                  Remaining:{" "}
                                  <strong className="text-success">
                                    {aiUsage.remaining === -1 ? (
                                      <span
                                        className="ai-usage-infinity"
                                        aria-label="Unlimited"
                                      >
                                        &#8734;
                                      </span>
                                    ) : (
                                      aiUsage.remaining
                                    )}
                                  </strong>
                                </span>
                              </div>
                            )}

                            <button
                              type="submit"
                              className="btn w-100 py-2.5 bg_blue_custom border-0 font-semibold"
                              disabled={
                                isGenerating ||
                                !prompt.trim() ||
                                (aiUsage.remaining !== -1 &&
                                  aiUsage.remaining <= 0)
                              }
                              style={{ borderRadius: "12px" }}
                            >
                              {isGenerating ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Generating Avatar...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                                  Generate Avatar
                                </>
                              )}
                            </button>
                          </form>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mb-4">
                        <div
                          className="card card-ai-generator p-4 shadow-sm h-100 d-flex flex-column align-items-center justify-content-center text-center"
                          style={{
                            borderStyle: generatedAvatarUrl
                              ? "solid"
                              : "dashed",
                          }}
                        >
                          {generatedAvatarUrl ? (
                            <div className="w-100">
                              <h5 className="font-semibold mb-3">
                                Your Generated Avatar
                              </h5>
                              <div className="mb-4 position-relative d-inline-block">
                                <img
                                  src={generatedAvatarUrl}
                                  alt="Generated Avatar"
                                  className="rounded-circle shadow-lg"
                                  style={{
                                    width: "180px",
                                    height: "180px",
                                    objectFit: "cover",
                                    border: "4px solid var(--blue-color)",
                                  }}
                                />
                              </div>
                              <div>
                                <button
                                  className="btn bg_blue_custom w-100 mb-2 py-2.5 font-semibold"
                                  onClick={() => onSelect(generatedAvatarUrl)}
                                  style={{ borderRadius: "12px" }}
                                >
                                  Use this Avatar
                                </button>
                                <button
                                  className="btn btn-outline-secondary w-100 py-2"
                                  onClick={() => setGeneratedAvatarUrl("")}
                                  style={{ borderRadius: "12px" }}
                                >
                                  Try Again
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 text-muted">
                              <div className="mb-3">
                                <i
                                  className="fa-solid fa-image-portrait"
                                  style={{ fontSize: "4rem", color: "#dee2e6" }}
                                ></i>
                              </div>
                              <h5>No avatar generated yet</h5>
                              <p className="small mb-0">
                                Your newly generated AI avatar will appear here.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AvatarModal;
